import React, { useContext } from 'react';
import { EmptyData, ITableContainerProps, Table as T } from '@design-system';
import { nanoid } from 'nanoid';
import { formatDate } from 'helpers';
import fp from 'lodash/fp';
import { IContact } from '@cincel/client';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { WLContext } from '@contexts';

const HEADERS = [
  translations.COMMON.TABLE_HEADING_NAME,
  translations.COMMON.TABLE_HEADING_EMAIL,
  translations.COMMON.TABLE_HEADING_CREATED_AT,
];

export interface IListingProps extends ITableContainerProps {
  /**
   * Contacts listing.
   */
  data: IContact[];
  /**
   * On delete action.
   */
  onDelete: (contact: string) => void;
  /**
   * Callback function invoked when the page changes.
   */
  onPageChange: (page: number) => void;
  /**
   * Callback function invoked with the page-size changes.
   */
  onPageSizeChange: (size: number) => void;
  /**
   * On update action.
   */
  onUpdate: (contact: IContact) => void;
  /**
   * A numeric value indicating the current page.
   */
  page: number;
  /**
   * A numeric value indicating the page size.
   */
  pageSize: number;
  /**
   * Total registries count.
   */
  total: number;
}

export const Listing: React.FC<IListingProps> = (props): JSX.Element => {
  const {
    data,
    onDelete,
    onPageChange,
    onPageSizeChange,
    onUpdate,
    page,
    pageSize,
    total,
    ...rest
  } = props;

  const { color } = useContext(WLContext);

  const { t } = useTranslation();

  return (
    <>
      <T.Container {...rest}>
        {!fp.isEmpty(data) ? (
          <T>
            <T.Thead>
              <T.Tr>
                {HEADERS.map((header) => (
                  <T.Th key={nanoid()} scope="col">
                    {t(header)}
                  </T.Th>
                ))}
                <T.Th className="min-w-[60px]" scope="col" />
              </T.Tr>
            </T.Thead>

            <T.Tbody>
              {data?.map((item: IContact) => (
                <T.Tr key={nanoid()} className="even:bg-gray-25">
                  <T.Td
                    className={'cursor-pointer font-medium'}>
                    {item?.name}
                  </T.Td>

                  <T.Td>{item?.email}</T.Td>

                  <T.Td>{formatDate(item?.created_at)}</T.Td>

                  <T.TdActions
                    brandColor={color}
                    isOwner
                    onDelete={() => onDelete(item.uuid)}
                    onUpdate={() => onUpdate(item)}
                  />
                </T.Tr>
              ))}
            </T.Tbody>
          </T>
        ) : (
          <EmptyData>{t(translations.CONTACTS.TABLE_EMPTY)}</EmptyData>
        )}
      </T.Container>

      <T.Pagination
        className="mt-4"
        currentPage={page}
        onPageChange={onPageChange}
        onSizeChange={onPageSizeChange}
        pageSize={pageSize}
        primaryColor={color}
        totalCount={total}
      />
    </>
  );
};
